/**
 * useLocalTemplate — resolves a page slug to a committed local template, or to
 * nothing so the caller falls back to the public page API.
 *
 * The list lookup is synchronous and needs no network, so a slug that has no
 * local template never delays the API request by even a tick.
 */

import { useQuery } from '@tanstack/react-query';
import { LOCAL_PAGES } from '../localPages';
import { hasLocalTemplate, loadLocalTemplate } from '../utils/localTemplate';

export function useLocalTemplate(pageSlug) {
  const registered = hasLocalTemplate(pageSlug, LOCAL_PAGES);

  const { data, isPending } = useQuery({
    queryKey: ['local-template', pageSlug],
    queryFn: () => loadLocalTemplate(pageSlug),
    enabled: registered,
    retry: false,
    // The files are immutable for the life of a build; loadLocalTemplate does
    // its own revalidation against the server on each miss.
    staleTime: Infinity,
  });

  // A disabled query also reports `isPending`, so the registered check has to
  // come first or every ordinary page would sit on a skeleton forever.
  if (!registered) return { checking: false, template: null };
  if (isPending) return { checking: true, template: null };

  // `null` from the loader (missing folder, private page, unreadable files)
  // means fall through to the API, which is also what an error leaves here.
  return { checking: false, template: data || null };
}
